import { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Eraser, Type, PenTool, Upload, RotateCcw } from "lucide-react";
import { cn } from "../lib/utils";

const signatureFonts = [
  { name: "Dancing Script", style: "font-dancing" },
  { name: "Great Vibes", style: "font-vibes" },
  { name: "Pacifico", style: "font-pacifico" },
  { name: "Caveat", style: "font-caveat" },
];

export default function SignaturePad({ 
  value, 
  onChange, 
  className,
  primaryColor = "#047857"
}) {
  const sigCanvas = useRef(null);
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState("draw"); // draw, type, or upload
  const [typedName, setTypedName] = useState("");
  const [selectedFont, setSelectedFont] = useState(signatureFonts[0]);
  const [penColor, setPenColor] = useState(primaryColor);

  // Load existing signature
  useEffect(() => {
    if (value && sigCanvas.current && mode === "draw") {
      // If there's an existing signature, don't overwrite
    }
  }, [value, mode]);

  // Update pen color when primary color changes
  useEffect(() => {
    setPenColor(primaryColor);
  }, [primaryColor]);

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setTypedName("");
    onChange("");
  };

  const saveDrawnSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataUrl = sigCanvas.current.toDataURL("image/png");
      onChange(dataUrl);
    }
  };

  const generateTypedSignature = () => {
    if (!typedName.trim()) return;
    
    // Create canvas for typed signature
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 150;
    const ctx = canvas.getContext("2d");
    
    // Clear background (transparent)
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw text
    ctx.fillStyle = penColor;
    ctx.font = `48px ${selectedFont.name}, cursive`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
    
    const dataUrl = canvas.toDataURL("image/png");
    onChange(dataUrl);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert("File size should be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas to resize/normalize the signature
        const canvas = document.createElement("canvas");
        const maxWidth = 400;
        const maxHeight = 150;
        
        let width = img.width;
        let height = img.height;
        
        // Scale down if needed while maintaining aspect ratio
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        canvas.width = maxWidth;
        canvas.height = maxHeight;
        const ctx = canvas.getContext("2d");
        
        // Clear background (transparent)
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Center the image
        const x = (maxWidth - width) / 2;
        const y = (maxHeight - height) / 2;
        ctx.drawImage(img, x, y, width, height);
        
        const dataUrl = canvas.toDataURL("image/png");
        onChange(dataUrl);
      };
      img.src = event.target?.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className={cn("space-y-4", className)}>
      <Tabs value={mode} onValueChange={setMode} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="draw" data-testid="sig-draw-tab">
            <PenTool className="w-4 h-4 mr-2" />
            Draw
          </TabsTrigger>
          <TabsTrigger value="type" data-testid="sig-type-tab">
            <Type className="w-4 h-4 mr-2" />
            Type
          </TabsTrigger>
          <TabsTrigger value="upload" data-testid="sig-upload-tab">
            <Upload className="w-4 h-4 mr-2" />
            Upload
          </TabsTrigger>
        </TabsList>

        {/* Draw Mode */}
        <TabsContent value="draw" className="mt-4">
          <div className="space-y-3">
            {/* Pen Color */}
            <div className="flex items-center gap-3">
              <Label className="text-xs">Pen Color:</Label>
              <div className="flex gap-2">
                {["#000000", "#1f2937", "#047857", "#0369a1", "#7c3aed", "#047857"].map((color, idx) => (
                  <button
                    key={`${color}-${idx}`}
                    onClick={() => setPenColor(color)}
                    className={cn(
                      "w-6 h-6 rounded-full border-2 transition-transform hover:scale-110",
                      penColor === color ? "border-primary ring-2 ring-primary/30" : "border-gray-300"
                    )}
                    style={{ backgroundColor: color }}
                    data-testid={`pen-color-${idx}`}
                  />
                ))}
                <input
                  type="color"
                  value={penColor}
                  onChange={(e) => setPenColor(e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer"
                />
              </div>
            </div>

            {/* Signature Canvas */}
            <div className="border-2 border-dashed border-border rounded-lg bg-white overflow-hidden">
              <SignatureCanvas
                ref={sigCanvas}
                penColor={penColor}
                canvasProps={{
                  width: 400,
                  height: 150,
                  className: "w-full cursor-crosshair touch-none",
                  style: { touchAction: "none" },
                  "data-testid": "signature-canvas"
                }}
                onEnd={saveDrawnSignature}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Sign above using your mouse or touch
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSignature}
                data-testid="clear-signature-btn"
              >
                <Eraser className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Type Mode */}
        <TabsContent value="type" className="mt-4">
          <div className="space-y-4">
            {/* Name Input */}
            <div>
              <Label>Your Name</Label>
              <Input
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Type your name"
                className="mt-1"
                data-testid="typed-signature-input"
              />
            </div>

            {/* Font Selection */}
            <div>
              <Label className="text-xs">Choose Style:</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {signatureFonts.map((font) => (
                  <button
                    key={font.name}
                    onClick={() => setSelectedFont(font)}
                    className={cn(
                      "p-3 rounded-lg border-2 text-center transition-all",
                      selectedFont.name === font.name 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:border-primary/50"
                    )}
                    style={{ fontFamily: `${font.name}, cursive` }}
                    data-testid={`font-${font.name.toLowerCase().replace(" ", "-")}`}
                  >
                    <span className="text-xl" style={{ color: penColor }}>
                      {typedName || "Your Name"}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <Button
              onClick={generateTypedSignature}
              disabled={!typedName.trim()}
              className="w-full"
              data-testid="generate-signature-btn"
            >
              Generate Signature
            </Button>
          </div>
        </TabsContent>

        {/* Upload Mode */}
        <TabsContent value="upload" className="mt-4">
          <div className="space-y-4">
            <div 
              className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium">Click to upload signature</p>
              <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 2MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="signature-file-input"
              />
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Upload a scanned image of your signature
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Signature Preview */}
      {value && (
        <div className="p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Your Signature
            </Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSignature}
              className="h-7 text-xs"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Reset
            </Button>
          </div>
          <div className="bg-white rounded border border-border p-2 flex justify-center">
            <img 
              src={value} 
              alt="Your signature" 
              className="h-16 object-contain"
              data-testid="signature-preview"
            />
          </div>
        </div>
      )}
    </div>
  );
}
